import * as React from 'react';

import * as Sentry from '@sentry/browser';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { Alert, Button, ButtonContainer, Spacer } from '@emburse/ui-core';

import { setupIntentRequiresMicrodeposits } from '@ReactHooks/useStripeSubscriptionAndCheckout';

interface ICheckoutFormProps {
  showSubscribe?: boolean;
  onUpdatePaymentMethod: (paymentMethodId: string) => void;
  onShowMicrodepositForm?: (setupIntent: any) => void;
  onCancel: () => void;
  onPaymentSuccess?: () => Promise<void>;
}

const CheckoutForm: React.FC<ICheckoutFormProps> = ({
  showSubscribe,
  onUpdatePaymentMethod,
  onShowMicrodepositForm,
  onCancel,
  onPaymentSuccess
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = React.useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (elements === null) {
      return;
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      // Show error to your customer
      setErrorMessage(submitError.message);
      return;
    }

    const res = await stripe?.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: 'if_required'
    });

    if (!res) {
      return;
    }

    if (!res?.setupIntent) {
      setErrorMessage(res.error.message || '');
      return;
    }

    if (onShowMicrodepositForm && setupIntentRequiresMicrodeposits(res?.setupIntent)) {
      onShowMicrodepositForm(res?.setupIntent);
      return;
    }

    // Call onPaymentSuccess before onUpdatePaymentMethod if provided
    if (onPaymentSuccess) {
      try {
        await onPaymentSuccess();
      } catch (error) {
        Sentry.captureException(error);
      }
    }
    await onUpdatePaymentMethod(res.setupIntent.payment_method as string);
  };

  const buttons = showSubscribe ? (
    <Button type="submit" disabled={!stripe || !elements} style={{ width: '100%' }} className="stripe-subscribe">
      Subscribe
    </Button>
  ) : (
    <>
      <Button onClick={onCancel} variant="outlined" className="stripe-cancel">
        Cancel
      </Button>
      <Button type="submit" disabled={!stripe || !elements} className="stripe-pay">
        Pay
      </Button>
    </>
  );

  return (
    <form onSubmit={handleSubmit}>
      {errorMessage && (
        <div>
          <Alert severity="error" className="alert-msg">
            {errorMessage}
          </Alert>
          <Spacer size={10} />
        </div>
      )}

      <PaymentElement />
      <Spacer size={10} />
      <ButtonContainer alignButtons="right">{buttons}</ButtonContainer>
    </form>
  );
};

interface IStripeCheckoutProps {
  subscription: any;
  checkoutSession: any;
  showSubscribe?: boolean;
  onUpdatePaymentMethod: (paymentMethodId: string) => void;
  onShowMicrodepositForm?: (setupIntent: any) => void;
  onCancel: () => void;
  onPaymentSuccess?: () => Promise<void>;
}

export const StripeCheckout: React.FC<IStripeCheckoutProps> = ({ subscription, checkoutSession, ...checkoutFormProps }) => {
  const stripePromise = window.Stripe(window.stripeConfig.publicKey);

  const options = {
    supportedCountries: ['US'],
    subscription: subscription,
    currency: 'usd',
    client_reference_id: subscription.id,
    clientSecret: checkoutSession.client_secret,
    // Fully customizable with appearance API.
    appearance: {
      /*...*/
    }
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm {...checkoutFormProps} />
    </Elements>
  );
};
